/**
 * Boots the Tzamun support widget for a signed-in user, and keeps its theme and
 * language in step with the app.
 *
 * Rendered INSIDE PrivateRoute, which is the point: the thirteen public routes
 * (quotation links, invoice/receipt views, /payment, /book, /confirm, /f) are
 * opened by the GARAGE's customers following an SMS link. They are not Tzamun's
 * customers, and their garage never agreed to route them at Tzamun IT — a
 * support bubble there collects "where is my car?" from the wrong people.
 *
 * Renders nothing. The launcher is the header help button; see HelpButton —
 * which is also why the widget does not need to be loaded up front: nothing on
 * the page is the launcher, so there is nothing to keep warm.
 */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import * as chatwoot from '../services/chatwoot';

// The build stamp already in index.html — no second source of truth to drift.
const appVersion = () => document.querySelector('meta[name="build-version"]')?.content || null;

export default function SupportChat() {
  const user = useSelector((state) => state.auth.user);
  const { theme } = useTheme();
  const { i18n } = useTranslation();
  const [started, setStarted] = useState(false);
  // The identity is fetched once per signed-in user. A ref, not state: it must
  // gate the effect without re-triggering it.
  const fetchedFor = useRef(null);

  useEffect(() => {
    if (!user?.id) return undefined;
    if (fetchedFor.current === user.id) return undefined;
    fetchedFor.current = user.id;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/auth/chat-identity');
        // null when CHATWOOT_HMAC_TOKEN is not set on this server. The widget
        // then never loads at all — it cannot run without identity validation.
        if (cancelled || !data?.chatwoot) return;
        // prepare(), not start(): this marks support available and stores the
        // identity, but does NOT download the Chatwoot SDK. The SDK and its
        // handshake were four-to-five requests plus a script on EVERY
        // authenticated page, for a panel most people never open. The help
        // drawer gates its support entry on isConfigured(), which prepare()
        // still sets immediately, so nothing disappears from the UI — the boot
        // simply happens on the first open() instead.
        const ok = chatwoot.prepare(data.chatwoot, {
          theme,
          locale: i18n.language,
          attributes: { app_version: appVersion() },
        });
        if (!cancelled) setStarted(ok);
      } catch {
        // Support chat is never worth breaking a page over, and the user has
        // no action to take if it fails. Silent by design.
      }
    })();

    return () => {
      cancelled = true;
    };
    // theme/locale deliberately absent: they are applied at start and then kept
    // in step by the two effects below. Including them here would re-run the
    // fetch on every toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Follow MotorMind, not the operating system. Chatwoot's own darkMode:'auto'
  // reads the OS, which would put a dark widget on a light MotorMind page.
  useEffect(() => {
    if (started) chatwoot.setColorScheme(theme);
  }, [started, theme]);

  useEffect(() => {
    if (started) chatwoot.setLocale(i18n.language);
  }, [started, i18n.language]);

  // Logout clears the token before this unmounts; reset here so the next person
  // on this browser does not inherit the conversation.
  useEffect(() => {
    if (started && !user?.id) {
      chatwoot.reset();
      setStarted(false);
      fetchedFor.current = null;
    }
  }, [started, user?.id]);

  return null;
}
